import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    fetchHeroData();
  }, []);

  const fetchHeroData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/sections?page=home`);
      const hero = response.data.find(section => section.section_key === 'hero');
      setHeroData(hero);
      setLoading(false);
      setIsVisible(true);
    } catch (error) {
      console.error('Error fetching hero data:', error);
      // Fallback data if API fails
      setHeroData({
        content: {
          title: 'Welcome to',
          highlight: 'Ambition.',
          background_type: 'video',
          background_video: 'https://assets.mixkit.co/videos/preview/mixkit-aerial-view-of-a-modern-city-at-night-41538-large.mp4',
          background_image: '',
          overlay_opacity: 0.5,
          text_color: '#ffffff'
        }
      });
      setLoading(false);
      setIsVisible(true);
    }
  };

  useEffect(() => {
    // Ensure video plays
    if (videoRef.current && heroData?.content?.background_type === 'video') {
      videoRef.current.play().catch(error => {
        console.log('Video autoplay prevented:', error);
      });
    }
  }, [heroData]);

  if (loading || !heroData) {
    return (
      <section className="relative h-screen w-full overflow-hidden bg-gray-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </section>
    );
  }

  const { content } = heroData;
  const overlayOpacity = content.overlay_opacity || 0.5;

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        {content.background_type === 'video' && content.background_video && (
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              transform: isVisible ? 'scale(1)' : 'scale(1.1)',
              transition: 'transform 1.5s ease-out',
              animation: isVisible ? 'zoomIn 1.5s ease-out, slowZoom 20s ease-in-out infinite' : 'none'
            }}
            poster={content.background_image}
          >
            <source src={content.background_video} type="video/mp4" />
            <source src={content.background_video} type="video/webm" />
          </video>
        )}
        
        {content.background_type === 'image' && content.background_image && (
          <img
            src={content.background_image}
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              transform: isVisible ? 'scale(1)' : 'scale(1.1)',
              transition: 'transform 1.5s ease-out',
              animation: isVisible ? 'zoomIn 1.5s ease-out, slowZoom 20s ease-in-out infinite' : 'none'
            }}
          />
        )}

        {content.background_type === 'color' && (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: content.background_color || '#0A1A50' }}
          />
        )}
        
        {/* Overlay */}
        <div 
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative h-full flex items-center justify-center text-center px-6">
        <div 
          className="max-w-4xl"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 1s ease-out 0.3s, transform 1s ease-out 0.3s'
          }}
        >
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light tracking-wide drop-shadow-2xl animate-fade-in px-4"
            style={{ 
              color: content.text_color || '#ffffff',
              animation: isVisible ? 'fadeInUp 1.2s ease-out' : 'none'
            }}
          >
            <span style={{
              display: 'inline-block',
              animation: isVisible ? 'fadeInUp 1s ease-out' : 'none'
            }}>
              {content.title}
            </span>{' '}
            <span 
              className="text-[#0A1A50] font-normal inline-block"
              style={{
                animation: isVisible ? 'fadeInUp 1s ease-out 0.3s both, pulse 2s ease-in-out 1.5s infinite' : 'none'
              }}
            >
              {content.highlight}
            </span>
          </h1>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div 
          className="w-6 h-10 border-2 rounded-full flex items-start justify-center p-2"
          style={{ borderColor: content.text_color || '#ffffff' }}
        >
          <div 
            className="w-1.5 h-3 rounded-full"
            style={{ backgroundColor: content.text_color || '#ffffff' }}
          ></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;